import React from 'react';
import PropTypes from 'prop-types';
import { IoMdInformationCircleOutline, IoIosArrowForward } from 'react-icons/io';
import { FiCheck } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { useToggle } from 'utils/hooks/useToggle';
import { ROUTES } from 'utils/constants';
import womanBall from 'assets/images/woman-ball.webp';
import vulva from 'assets/images/mid-vulva.webp';

import AdditionalContent from '../AdditionalContent';
import LockedIcon from '../LockedIcon';
import EvieGreeting from '../EvieGreeting';
import UnitDescriptionModal from '../UnitDescriptionModal';
import { useEvieAppContext } from '../EvieAppProvider';
import { useEvieHomeContext } from '../EvieHomeProvider';
import { ACTIVITY_TYPES, HOME_STATUS, getSqueezeDuration } from '../utils';

import './program-home.scss';

const ProgramHome = ({ locked }) => {
  const { t } = useTranslation();
  const { push } = useHistory();
  const {
    program: { programLevels = [] } = {},
    personalInfo: { currentProgram, currentWeek, currentDay, dayContent },
  } = useEvieAppContext();
  const { changeHomeStatus, setActivityType, selectTask } = useEvieHomeContext();
  const [isUnitDescriptionOpen, toggleUnitDescription] = useToggle();

  const currentLevel = programLevels.find(({ number }) => number === currentWeek) || {};
  const isSessionDoneForToday =
    !locked && dayContent.length > 0 && dayContent.every((task) => task.completedSessionStatus);

  const handleGoToPayment = () => {
    push(`${ROUTES.EVIE_ONBOARDING}?view=plans`);
  };

  const handleSelectTask = (currentTask) => {
    if (locked) {
      handleGoToPayment();
      return;
    }
    const isExercise = currentTask.__typename === ACTIVITY_TYPES.EXERCISE;
    selectTask(currentTask);
    setActivityType(isExercise ? ACTIVITY_TYPES.EXERCISE : ACTIVITY_TYPES.SQUEEZE);
    changeHomeStatus(isExercise ? HOME_STATUS.SESSION : HOME_STATUS.SQUEEZE);
  };

  return (
    <section className="evie-my-program">
      <section className="evie-my-program__content">
        {locked ? <h2 className="evie-profile__header_title">{t('evieApp.program.title')}</h2> : <EvieGreeting />}
        <section className="evie-my-program__info">
          <header className="evie-my-program__infoheader">
            <div>
              <h3 className="evie-my-program__infoheader_title">{currentProgram}</h3>
              <p className="evie-my-program__infoheader_subtitle">
                {t('evieApp.program.levelLabel')} {currentWeek} / {t('evieApp.program.sessionLabel')} {currentDay}
              </p>
              {isSessionDoneForToday && (
                <p className="evie-my-program__infoheader_donetoday">{t('evieApp.program.doneForToday')}</p>
              )}
            </div>
            <button
              type="button"
              className="evie-my-program__infoheader_infobtn"
              aria-label={t('evieApp.program.unitInfoLabel')}
              onClick={toggleUnitDescription}
            >
              <IoMdInformationCircleOutline size={28} />
            </button>
          </header>
          <ul className="evie-my-program__infocontent">
            {dayContent.map((currentTask) => {
              const { __typename, id, title, exercises, squeezes, completedSessionStatus, laps = 1 } = currentTask;
              const isExercise = __typename === ACTIVITY_TYPES.EXERCISE;
              const exDuration =
                ((exercises || []).reduce((acc, { exercise: { duration } }) => acc + Number(duration), 0) / 60) * laps;
              const sqDuration = getSqueezeDuration(squeezes) / 60;
              const taskDuration = Math.ceil(isExercise ? exDuration : sqDuration);

              return (
                <li key={id} className="evie-my-program__infocontent_block">
                  <div
                    className={`evie-my-program__infocontent_check ${
                      completedSessionStatus ? 'evie-my-program__infocontent_check--completed' : ''
                    }`}
                  >
                    {completedSessionStatus && <FiCheck size={12} />}
                  </div>
                  <button
                    type="button"
                    className="evie-my-program__infocontent_card"
                    onClick={() => handleSelectTask(currentTask)}
                  >
                    <div
                      className={`evie-my-program__infocontent_eximg ${
                        completedSessionStatus ? '' : 'not-completed-task'
                      }`}
                      style={locked ? undefined : { backgroundImage: `url(${isExercise ? womanBall : vulva})` }}
                    />
                    <div>
                      <h3 className="evie-my-program__infocontent_title">{title}</h3>
                      <span className="evie-my-program__infocontent_duration">{taskDuration} minutes</span>
                    </div>
                    <div className="evie-my-program__infocontent_icons">
                      {locked && <LockedIcon />}
                      <IoIosArrowForward size={20} />
                    </div>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      </section>
      <section className="evie-my-program__additionalcontent">
        <AdditionalContent isProgram />
      </section>
      {isUnitDescriptionOpen && (
        <UnitDescriptionModal
          modalIsOpen={isUnitDescriptionOpen}
          toggleModal={toggleUnitDescription}
          name={currentLevel.name}
          description={currentLevel.description}
        />
      )}
    </section>
  );
};

ProgramHome.propTypes = {
  locked: PropTypes.bool,
};

ProgramHome.defaultProps = {
  locked: false,
};

export default ProgramHome;
